import React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RequirementsView, ViewAudience } from './requirements';
import { closeRequirements } from './useRequirements';
import { AccessText, DerivedNote, FONT, LINE, SURFACE, TEXT, TEXT_3 } from './ui';

/** One lesson's requirements, from its icon by the lesson name: what each audience of the course must do. */
const RequirementsSummary: React.FC<{
  moduleId: string;
  lessonId: string;
  /** The lesson as the editor names it, while the view is read or when it doesn't hold the lesson. */
  title: string;
  view: RequirementsView | null;
  loading: boolean;
  error?: string | null;
  /** The view is the backend's, drawn from the live lessons (else the editor's own, DUMMY). */
  live?: boolean;
}> = ({ moduleId, lessonId, title, view, loading, error = null, live = false }) => {
  const modules = view?.modules ?? [];
  // Its module, else wherever the lesson is now.
  const own = modules.findIndex((m) => m.id === moduleId && m.lessons.some((x) => x.id === lessonId));
  const mi = own >= 0 ? own : modules.findIndex((m) => m.lessons.some((x) => x.id === lessonId));
  const li = mi >= 0 ? modules[mi].lessons.findIndex((x) => x.id === lessonId) : -1;
  const l = li >= 0 ? modules[mi].lessons[li] : null;
  // The backend reads the live lessons: one added since the last publish isn't there yet.
  const unpublished = live && Boolean(view) && !loading && !error && !l;
  if (!l && !live) return null;
  const row = (a: ViewAudience) => (
    <Box
      key={a.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 1,
        borderTop: `1px solid ${LINE}`
      }}
    >
      <Typography noWrap sx={{ fontFamily: FONT, fontSize: 13, color: TEXT, minWidth: 0 }}>
        {a.name}
      </Typography>
      {l?.cells[a.id] && <AccessText audienceName={a.name} access={l.cells[a.id]} />}
    </Box>
  );

  return (
    <Dialog
      open
      onClose={closeRequirements}
      maxWidth={false}
      PaperProps={{
        sx: {
          background: SURFACE,
          border: `1px solid ${LINE}`,
          borderRadius: '14px',
          width: 'min(400px, calc(100vw - 32px))'
        }
      }}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, color: TEXT }}>
            {l?.title ?? title}
          </Typography>
          {l && (
            <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_3, mt: 0.25 }}>
              Lesson {mi + 1}.{li + 1}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Close" onClick={closeRequirements} sx={{ color: TEXT_3, p: 0.5 }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box sx={{ px: 2.5, pb: 1.5 }}>
        {loading || error || !view ? (
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_3, py: 1.5 }}>
            {error || 'Loading the audiences...'}
          </Typography>
        ) : unpublished ? (
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_3, py: 1.5 }}>
            This lesson isn’t published yet. Its requirements show once it is.
          </Typography>
        ) : (
          <>
            {view.derived && <DerivedNote sx={{ pb: 1 }} />}
            {view.readAssess.map(row)}
            {view.readOnly.map(row)}
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default RequirementsSummary;
